import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import NoChartDataView from "../no-chart-data-view/no-chart-data-view";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface IProps {
  borderColor1: string;
  borderColor2?: string;
  borderColor3?: string;
  borderColor4?: string;
  borderColor5?: string;
  backgroundColor1: string;
  backgroundColor2?: string;
  backgroundColor3?: string;
  backgroundColor4?: string;
  backgroundColor5?: string;
  label1: string;
  label2?: string;
  label3?: string;
  label4?: string;
  label5?: string;
  data1: Array<number | null>;
  data2?: Array<number | null>;
  data3?: Array<number | null>;
  data4?: Array<number | null>;
  data5?: Array<number | null>;
  labels: Array<string | number>;
  yAxisUnitLeftSide: string;
  yAxisUnitRightSide?: string;
  xAxisLabel?: string;
  labelCallback?: (tooltipItem: any) => string;
  titleCallback?: (tooltipItems: any[]) => string;
  xLabelCallback?: (
    value: string | number,
    index: number,
    values: any,
  ) => string;
  isChartDataAvailable?: boolean;
  maxBarThickness?: number;
}

const MultipleChartType = (props: IProps) => {
  let labels = props.labels;
  let adjustedData1 = props.data1;
  let adjustedData2 = props.data2;
  let adjustedData3 = props.data3;
  let adjustedData4 = props.data4;
  let adjustedData5 = props.data5;

  const minBars = 8;
  if (labels.length > 0 && labels.length < minBars) {
    const diff = minBars - labels.length;
    labels = [...labels, ...Array(diff).fill("")];
    if (adjustedData1)
      adjustedData1 = [...adjustedData1, ...Array(diff).fill(null)];
    if (adjustedData2)
      adjustedData2 = [...adjustedData2, ...Array(diff).fill(null)];
    if (adjustedData3)
      adjustedData3 = [...adjustedData3, ...Array(diff).fill(null)];
    if (adjustedData4)
      adjustedData4 = [...adjustedData4, ...Array(diff).fill(null)];
    if (adjustedData5)
      adjustedData5 = [...adjustedData5, ...Array(diff).fill(null)];
  }

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        align: "start" as const,
        labels: {
          usePointStyle: true,
          pointStyleWidth: 10,
          boxHeight: 7,
          font: {
            size: 14,
            family: "Poppins",
            weight: "normal" as const,
          },
          color: "#383941",
          generateLabels: (chart: any) => {
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            labels.forEach((label: any) => {
              switch (label.datasetIndex) {
                case 0:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor1;
                  break;
                case 1:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor2;
                  break;
                case 2:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor3;
                  break;
                case 3:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor4;
                  break;
                case 4:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor5;
                  break;
                default:
                  label.pointStyle = "circle";
                  label.fillStyle = "";
                  break;
              }
            });
            return labels;
          },
        },
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: props.titleCallback,
          label: props.labelCallback,
        },
      },
      centerText: false,
    },
    scales: {
      x: {
        title: {
          text: props.xAxisLabel,
          display: props.xAxisLabel ? true : false,
          color: "#69768B",
          font: {
            family: "Poppins",
            size: 12,
            weight: "normal" as const,
          },
        },
        ticks: {
          callback: props.xLabelCallback,
        },
        grid: {
          display: false,
        },
      },
      "left-y-axis": {
        type: "linear" as const,
        position: "left" as const,
        beginAtZero: true,
        ticks: {
          color: "#69768B",
          callback: (value: any) => `${value} ${props.yAxisUnitLeftSide}`,
        },
      },
      ...(props.yAxisUnitRightSide && {
        "right-y-axis": {
          type: "linear" as const,
          position: "right" as const,
          beginAtZero: true,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            color: "#69768B",
            callback: (value: any) => `${value} ${props.yAxisUnitRightSide}`,
          },
        },
      }),
    },
    datasets: {
      bar: {
        maxBarThickness:
          props.maxBarThickness !== undefined ? props.maxBarThickness : 40,
      },
    },
  };

  const legendMargin = {
    id: "legendMargin",
    beforeInit: (chart: any) => {
      const fitValue = chart.legend.fit;
      chart.legend.fit = () => {
        fitValue.bind(chart.legend)();
        return (chart.legend.height += 30);
      };
    },
  };

  const barBorderRadius = {
    topLeft: 6,
    topRight: 6,
    bottomLeft: 0,
    bottomRight: 0,
  };

  const data = {
    labels: labels,
    datasets: [
      {
        type: "bar" as const,
        label: props.label1,
        data: adjustedData1,
        borderColor: props.borderColor1,
        backgroundColor: props.backgroundColor1,
        borderRadius: barBorderRadius,
        yAxisID: "left-y-axis",
      },
      {
        type: "bar" as const,
        label: props.label2,
        data: adjustedData2,
        borderColor: props.borderColor2,
        backgroundColor: props.backgroundColor2,
        borderRadius: barBorderRadius,
        yAxisID: "left-y-axis",
      },
      {
        type: "bar" as const,
        label: props.label3,
        data: adjustedData3,
        borderColor: props.borderColor3,
        backgroundColor: props.backgroundColor3,
        borderRadius: barBorderRadius,
        yAxisID: "left-y-axis",
      },
      {
        type: "bar" as const,
        label: props.label4,
        data: adjustedData4,
        borderColor: props.borderColor4,
        backgroundColor: props.backgroundColor4,
        borderRadius: barBorderRadius,
        yAxisID: "left-y-axis",
      },
      {
        type: "bar" as const,
        label: props.label5,
        data: adjustedData5,
        borderColor: props.borderColor5,
        backgroundColor: props.backgroundColor5,
        fill: true,
        yAxisID: "left-y-axis",
      },
    ],
  };

  return (
    <div
      style={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        height: "100%",
      }}
    >
      <div
        style={{
          minWidth: "100%",
          width: "1000px",
          minHeight: props.isChartDataAvailable
            ? "calc(100vh - 300px)"
            : "calc(100vh - 190px)",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
        }}
      >
        {props.isChartDataAvailable ? (
          <Chart
            type="bar"
            data={data}
            options={options}
            plugins={[legendMargin]}
          />
        ) : (
          <NoChartDataView />
        )}
      </div>
    </div>
  );
};

export default MultipleChartType;
